import React, { useState, useEffect, useCallback, useRef } from "react";
import TablePagesTitle from "../../components/table-pages-title/TablePagesTitle";
import LoginAsUser from "../../components/loginasuser/LoginAsUser";
import { manualPaymentSync } from "../../locales/en";
import { useSelector } from "react-redux";
import privateAxios from "../../utils/axiosPrivate";
import {
  getManualSyncStatusUrl,
  getManualSyncStartUrl,
  getManualSyncLastSyncUrl
} from "../../utils/constants/apiEndPoints";
import { formatDateWithDayjs, showToast } from "../../utils/utils";
import {
  showToastType,
  SYNC_POLLING_INTERVAL
} from "../../utils/constants/appConstants";
import PaymentSyncLogsTable from "../../components/table/all-tables/paymentsynctable/PaymentSyncLogsTable";
import { commonErrMsg } from "../../locales/en";
import { FaSpinner } from "react-icons/fa";
import "../../assets/scss/common.scss";
import "./PaymentUpdates.scss";

const ManualPaymentSync = () => {
  const profile = useSelector((state) => state.userManagement.profile);

  // Local state
  const [selectedOption, setSelectedOption] = useState("due");
  const [quoteId, setQuoteId] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [startSyncLoading, setStartSyncLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const pollingRef = useRef(null);

  const syncOptions = [
    { key: manualPaymentSync?.value?.due },
    { key: manualPaymentSync?.value?.overdue },
    { key: manualPaymentSync?.value?.funded },
    { key: manualPaymentSync?.value?.particular }
  ];

  // Fetch last sync info
  const fetchLastSyncInfo = useCallback(async () => {
    try {
      const response = await privateAxios.get(getManualSyncLastSyncUrl);
      setLastSync(response?.data?.data || null);
    } catch {
      showToast(
        manualPaymentSync?.errors?.fetchLastSyncFailed || commonErrMsg?.item7,
        showToastType.error
      );
    }
  }, []);

  // Check initial sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      setInitialLoading(true);
      const response = await privateAxios.put(getManualSyncStatusUrl);
      const isInProgress = response?.data?.data || false;
      setIsSyncing(isInProgress);

      // Only fetch last sync info if NOT syncing
      if (!isInProgress) {
        fetchLastSyncInfo();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message ||
          manualPaymentSync?.errors?.fetchStatusFailed,
        showToastType.error
      );
    } finally {
      setInitialLoading(false);
    }
  }, [fetchLastSyncInfo]);

  // Start polling for sync status
  const startPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    pollingRef.current = setInterval(async () => {
      try {
        const response = await privateAxios.put(getManualSyncStatusUrl);
        const isInProgress = response?.data?.data || false;
        setIsSyncing(isInProgress);

        if (!isInProgress) {
          // Sync completed, stop polling and refresh last sync
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          fetchLastSyncInfo();
        }
      } catch (error) {
        setIsSyncing(false);
        showToast(
          error?.response?.data?.message ||
            manualPaymentSync?.errors?.fetchStatusFailed,
          showToastType.error
        );
      }
    }, SYNC_POLLING_INTERVAL);
  }, [fetchLastSyncInfo]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchSyncStatus();
    fetchLastSyncInfo();

    return () => {
      stopPolling();
    };
  }, [fetchSyncStatus, fetchLastSyncInfo, stopPolling]);

  // Handle sync start
  const handleSyncNow = async () => {
    if (
      selectedOption === manualPaymentSync?.value?.particular &&
      !quoteId.trim()
    ) {
      showToast(manualPaymentSync?.quoteId, showToastType.error);
      return;
    }

    try {
      setStartSyncLoading(true);
      const { option, userAccountId } = {
        option: selectedOption,
        userAccountId: profile?.id
      };
      const params = { option, userAccountId };
      if (selectedOption === manualPaymentSync?.value?.particular) {
        params.quoteId = quoteId;
      }

      const response = await privateAxios.post(getManualSyncStartUrl, null, {
        params
      });
      showToast(response?.data?.message, showToastType.success);

      // Start polling to check status
      setIsSyncing(true);
      startPolling();
    } catch (error) {
      showToast(
        error?.response?.data?.message ||
          manualPaymentSync?.errors?.startSyncFailed ||
          commonErrMsg?.item7,
        showToastType.error
      );
    } finally {
      setStartSyncLoading(false);
    }
  };

  // Handle option change
  const handleOptionChange = (option) => {
    setSelectedOption(option);
    if (option !== manualPaymentSync?.value?.particular) {
      setQuoteId("");
    }
  };

  // Format last sync date and time (from paymentSyncJob.updatedAt)
  const formatLastSyncDateTime = (dateString) => {
    if (!dateString) return "";
    return formatDateWithDayjs(dateString, "DD MMM, YYYY [at] HH:mm a");
  };

  return (
    <>
      <LoginAsUser />
      <TablePagesTitle mainTitle={manualPaymentSync?.mainTitle} />

      <div className="row mb-3 mx-0">
        <div className="col-12">
          {/* Sync in Progress Card - Simple View */}
          {(initialLoading || isSyncing) && (
            <div className="sync-progress-card">
              <div className="d-flex align-items-center gap-3">
                <div>
                  <h5 className="sync-progress-title">
                    {initialLoading ? "" : manualPaymentSync?.syncInProgress}
                  </h5>
                  <p className="sync-progress-subtitle">
                    {initialLoading ? "" : manualPaymentSync?.syncProgressNote}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Full Sync Controls Card - Show when not syncing and not loading */}
          {!initialLoading && !isSyncing && (
            <>
              <div className="sync-controls-card">
                {/* No Sync in Progress Message */}
                <div className="no-sync-message">
                  {manualPaymentSync?.noSyncInProgress}
                </div>

                {/* Sync Options Section */}
                <div className="sync-options-section">
                  <div className="sync-dropdown-wrapper">
                    <label htmlFor="syncOption" className="sync-label">
                      {manualPaymentSync?.sync}
                    </label>
                    <select
                      id="syncOption"
                      className="sync-dropdown"
                      value={selectedOption}
                      onChange={(e) => handleOptionChange(e.target.value)}
                      disabled={startSyncLoading}
                    >
                      {syncOptions.map(({ key }) => (
                        <option
                          key={key}
                          value={manualPaymentSync?.value?.[key]}
                        >
                          {manualPaymentSync?.option?.[key]}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedOption === "particular" && (
                    <div className="quote-id-input-block">
                      <input
                        type="text"
                        id="quoteId"
                        placeholder={manualPaymentSync?.enterQuoteId}
                        value={quoteId}
                        onChange={(e) => setQuoteId(e.target.value)}
                        disabled={startSyncLoading}
                      />
                    </div>
                  )}

                  {/* Note Text */}
                  {selectedOption !== "particular" && (
                    <div className="sync-note">{manualPaymentSync?.note}</div>
                  )}
                </div>

                {/* Sync Button Section */}
                <div className="sync-button-section">
                  <button
                    className="btn btn-primary sync-now-btn"
                    onClick={handleSyncNow}
                    disabled={startSyncLoading}
                  >
                    {startSyncLoading ? (
                      <>
                        <FaSpinner className="btn-spinner" />
                        {manualPaymentSync?.syncInProgress}
                      </>
                    ) : (
                      manualPaymentSync?.syncNow
                    )}
                  </button>
                </div>

                {/* Last Sync Result Section */}
                {lastSync?.paymentSyncJob?.updatedAt && (
                  <div className="last-sync-result">
                    <div className="result-header">
                      {`${manualPaymentSync?.resultOfLastSync} `}
                      {formatLastSyncDateTime(
                        lastSync.paymentSyncJob.updatedAt
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sync Logs Table */}
              <div className="sync-logs-section mt-4">
                <PaymentSyncLogsTable />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ManualPaymentSync;
